import { z } from "zod/v4"

/**
 * 汎用的な期間を表す型
 * 開始時刻と終了時刻（省略可能）を持つ
 */
export const Period = z.object({
  start_at: z.number().describe("開始日時 (UNIX Timestamp mills)"),
  end_at: z.number().nullable().describe("終了日時 (UNIX Timestamp mills、nullの場合は無期限)"),
})

export type Period = z.infer<typeof Period>

/**
 * 期間の状態を表す型
 * システムが自動計算・管理する
 */
export const PeriodState = z.object({
  active: z.boolean().describe("現在期間内かどうか"),
  next_change_at: z.number().nullable().describe("次の期間状態変更時刻（開始・終了）"),
})

export type PeriodState = z.infer<typeof PeriodState>

/**
 * API リクエスト用の期間型（camelCase）
 */
export const PeriodRequest = z.object({
  startAt: z.number().describe("開始日時 (UNIX Timestamp mills)"),
  endAt: z.number().nullable().describe("終了日時 (UNIX Timestamp mills、nullの場合は無期限)"),
})

export type PeriodRequest = z.infer<typeof PeriodRequest>

/**
 * 期間の状態を計算する汎用関数
 */
export function calculatePeriodState(
  period: Period,
  now: number
): PeriodState {
  const active = period.start_at <= now && 
    (period.end_at === null || now < period.end_at)
  
  const next_change_at = 
    now < period.start_at ? period.start_at :
    period.end_at && now < period.end_at ? period.end_at :
    null
  
  return { active, next_change_at }
}

/**
 * 猶予期間付きで期間の状態を計算する関数
 * start_at よりも graceMs ミリ秒前に active を true にする
 * end_at での非アクティブ化には猶予期間を適用しない
 */
export function calculatePeriodStateWithGrace(
  period: Period,
  now: number,
  graceMs: number
): PeriodState {
  const graceStart = period.start_at - graceMs
  const active = graceStart <= now && 
    (period.end_at === null || now < period.end_at)
  
  const next_change_at = 
    now < graceStart ? graceStart :
    period.end_at && now < period.end_at ? period.end_at :
    null
  
  return { active, next_change_at }
}

/**
 * ショップ系コンテンツ（カスタムタグ・バナー）の表示期間猶予時間（1時間）
 * デーモンの処理遅延を考慮し、start_at の1時間前に active = true にする
 * クライアント側で start_at / end_at による正確な表示制御を行う前提
 */
export const SHOP_DISPLAY_PERIOD_GRACE_MS = 60 * 60 * 1000
